import { useEffect, useState, useCallback } from 'react'
import {
  Activity, Plus, RefreshCw, AlertTriangle, Play, CheckCircle2,
  Clock, Calendar, ShieldAlert, X, Loader2,
} from 'lucide-react'
import styles from './DRDrillsPage.module.css'

const DR_API = import.meta.env.VITE_DR_API_BASE_URL ?? ''

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('dr_token')
  return token
    ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
    : { 'Content-Type': 'application/json' }
}

// ── Types ─────────────────────────────────────────────────────────────────────

type DrillStatus = 'SCHEDULED' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED' | 'CANCELLED'
type DrillType = 'FAILOVER' | 'TABLETOP' | 'SIMULATION' | 'BACKUP_RESTORE'
type DrillOutcome = 'SUCCESS' | 'PARTIAL_SUCCESS' | 'FAILURE'

interface Drill {
  id: number
  title: string
  product_name: string
  drill_type: DrillType
  status: DrillStatus
  outcome: DrillOutcome | null
  scheduled_date: string
  started_at: string | null
  completed_at: string | null
  duration_minutes: number | null
  sla_compliant: boolean | null
}

interface DrillTemplate {
  id: number
  name: string
  drill_type: DrillType
  estimated_duration_hours: number
}

interface Product {
  id: number
  product_name: string
}

// ── Create drill form ─────────────────────────────────────────────────────────

interface CreateDrillForm {
  title: string
  product: string
  drill_type: DrillType | ''
  scheduled_date: string
  template: string
  notes: string
}

// ── Complete drill form ───────────────────────────────────────────────────────

interface CompleteDrillForm {
  outcome: DrillOutcome | ''
  actual_rpo_hours: string
  actual_rto_hours: string
  lessons_learned: string
  postmortem_notes: string
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const STATUS_LABELS: Record<DrillStatus, string> = {
  SCHEDULED: 'Scheduled',
  IN_PROGRESS: 'In Progress',
  COMPLETED: 'Completed',
  FAILED: 'Failed',
  CANCELLED: 'Cancelled',
}

const STATUS_FILTERS: Array<{ key: string; label: string }> = [
  { key: 'ALL', label: 'All' },
  { key: 'SCHEDULED', label: 'Scheduled' },
  { key: 'IN_PROGRESS', label: 'In Progress' },
  { key: 'COMPLETED', label: 'Completed' },
  { key: 'FAILED', label: 'Failed' },
]

const DRILL_TYPE_LABELS: Record<DrillType, string> = {
  FAILOVER: 'Failover',
  TABLETOP: 'Tabletop',
  SIMULATION: 'Simulation',
  BACKUP_RESTORE: 'Backup Restore',
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}


function StatusBadge({ status }: { status: DrillStatus }) {
  return (
    <span className={`${styles.statusBadge} ${styles[`status_${status}`]}`}>
      {STATUS_LABELS[status] ?? status}
    </span>
  )
}

function TypeBadge({ type }: { type: DrillType }) {
  return (
    <span className={styles.typeBadge}>
      {DRILL_TYPE_LABELS[type] ?? type}
    </span>
  )
}

function SlaBadge({ compliant }: { compliant: boolean | null }) {
  if (compliant === null) return null
  return compliant ? (
    <span className={styles.slaOk}>SLA Met</span>
  ) : (
    <span className={styles.slaBad}>SLA Missed</span>
  )
}

// ── Schedule Drill Modal ──────────────────────────────────────────────────────

function ScheduleDrillModal({
  products,
  templates,
  onCreated,
  onClose,
}: {
  products: Product[]
  templates: DrillTemplate[]
  onCreated: () => void
  onClose: () => void
}) {
  const [form, setForm] = useState<CreateDrillForm>({
    title: '',
    product: '',
    drill_type: '',
    scheduled_date: '',
    template: '',
    notes: '',
  })
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  function set<K extends keyof CreateDrillForm>(key: K, value: CreateDrillForm[K]) {
    setForm(f => ({ ...f, [key]: value }))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!form.title.trim() || !form.product || !form.drill_type || !form.scheduled_date) {
      setFormError('Title, product, type, and scheduled date are required.')
      return
    }
    setSubmitting(true)
    setFormError(null)
    try {
      const body: Record<string, unknown> = {
        title: form.title.trim(),
        product: Number(form.product),
        drill_type: form.drill_type,
        scheduled_date: form.scheduled_date,
        notes: form.notes.trim(),
      }
      if (form.template) body.template = Number(form.template)
      const res = await fetch(`${DR_API}/api/v1/dr/drills/`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(JSON.stringify(data))
      }
      onCreated()
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to create drill.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className={styles.modalOverlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            <Calendar size={16} /> Schedule DR Drill
          </h2>
          <button className={styles.modalClose} onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </div>

        {formError && (
          <div className={styles.formError}>
            <AlertTriangle size={13} /> {formError}
          </div>
        )}

        <form className={styles.modalBody} onSubmit={handleSubmit}>
          <div className={styles.formRow}>
            <label className={styles.formLabel}>Title *</label>
            <input
              className={styles.formInput}
              value={form.title}
              onChange={e => set('title', e.target.value)}
              placeholder="e.g. Q2 Failover Drill — Production"
              required
            />
          </div>

          <div className={styles.formRow}>
            <label className={styles.formLabel}>Product *</label>
            <select
              className={styles.formSelect}
              value={form.product}
              onChange={e => set('product', e.target.value)}
              required
            >
              <option value="">Select product…</option>
              {products.map(p => (
                <option key={p.id} value={p.id}>{p.product_name}</option>
              ))}
            </select>
          </div>

          <div className={styles.formRow}>
            <label className={styles.formLabel}>Drill Type *</label>
            <select
              className={styles.formSelect}
              value={form.drill_type}
              onChange={e => set('drill_type', e.target.value as DrillType)}
              required
            >
              <option value="">Select type…</option>
              <option value="FAILOVER">Failover</option>
              <option value="TABLETOP">Tabletop</option>
              <option value="SIMULATION">Simulation</option>
              <option value="BACKUP_RESTORE">Backup Restore</option>
            </select>
          </div>

          <div className={styles.formRow}>
            <label className={styles.formLabel}>Scheduled Date *</label>
            <input
              type="datetime-local"
              className={styles.formInput}
              value={form.scheduled_date}
              onChange={e => set('scheduled_date', e.target.value)}
              required
            />
          </div>

          <div className={styles.formRow}>
            <label className={styles.formLabel}>Template (optional)</label>
            <select
              className={styles.formSelect}
              value={form.template}
              onChange={e => set('template', e.target.value)}
            >
              <option value="">No template</option>
              {templates.map(t => (
                <option key={t.id} value={t.id}>
                  {t.name} ({DRILL_TYPE_LABELS[t.drill_type] ?? t.drill_type}, ~{t.estimated_duration_hours}h)
                </option>
              ))}
            </select>
          </div>

          <div className={styles.formRow}>
            <label className={styles.formLabel}>Notes</label>
            <textarea
              className={styles.formTextarea}
              value={form.notes}
              onChange={e => set('notes', e.target.value)}
              rows={3}
              placeholder="Optional drill notes or objectives…"
            />
          </div>

          <div className={styles.modalFooter}>
            <button type="button" className={styles.btnCancel} onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className={styles.btnPrimary} disabled={submitting}>
              {submitting
                ? <><Loader2 size={13} className={styles.spin} /> Scheduling…</>
                : <><Calendar size={13} /> Schedule Drill</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Complete Drill Modal ──────────────────────────────────────────────────────

function CompleteDrillModal({
  drillId,
  drillTitle,
  onCompleted,
  onClose,
}: {
  drillId: number
  drillTitle: string
  onCompleted: () => void
  onClose: () => void
}) {
  const [form, setForm] = useState<CompleteDrillForm>({
    outcome: '',
    actual_rpo_hours: '',
    actual_rto_hours: '',
    lessons_learned: '',
    postmortem_notes: '',
  })
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  function set<K extends keyof CompleteDrillForm>(key: K, value: CompleteDrillForm[K]) {
    setForm(f => ({ ...f, [key]: value }))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!form.outcome) {
      setFormError('Outcome is required.')
      return
    }
    setSubmitting(true)
    setFormError(null)
    try {
      const body: Record<string, unknown> = { outcome: form.outcome }
      if (form.actual_rpo_hours) body.actual_rpo_hours = parseFloat(form.actual_rpo_hours)
      if (form.actual_rto_hours) body.actual_rto_hours = parseFloat(form.actual_rto_hours)
      if (form.lessons_learned) body.lessons_learned = form.lessons_learned.trim()
      if (form.postmortem_notes) body.postmortem_notes = form.postmortem_notes.trim()

      const res = await fetch(`${DR_API}/api/v1/dr/drills/${drillId}/complete/`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(JSON.stringify(data))
      }
      onCompleted()
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to complete drill.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className={styles.modalOverlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            <CheckCircle2 size={16} /> Complete Drill
          </h2>
          <button className={styles.modalClose} onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <p className={styles.modalDrillName}>{drillTitle}</p>

        {formError && (
          <div className={styles.formError}>
            <AlertTriangle size={13} /> {formError}
          </div>
        )}

        <form className={styles.modalBody} onSubmit={handleSubmit}>
          <div className={styles.formRow}>
            <label className={styles.formLabel}>Outcome *</label>
            <select
              className={styles.formSelect}
              value={form.outcome}
              onChange={e => set('outcome', e.target.value as DrillOutcome)}
              required
            >
              <option value="">Select outcome…</option>
              <option value="SUCCESS">Success</option>
              <option value="PARTIAL_SUCCESS">Partial Success</option>
              <option value="FAILURE">Failure</option>
            </select>
          </div>

          <div className={styles.formRowGroup}>
            <div className={styles.formRow}>
              <label className={styles.formLabel}>Actual RPO (hours)</label>
              <input
                type="number"
                min="0"
                step="0.1"
                className={styles.formInput}
                value={form.actual_rpo_hours}
                onChange={e => set('actual_rpo_hours', e.target.value)}
                placeholder="e.g. 1.5"
              />
            </div>
            <div className={styles.formRow}>
              <label className={styles.formLabel}>Actual RTO (hours)</label>
              <input
                type="number"
                min="0"
                step="0.1"
                className={styles.formInput}
                value={form.actual_rto_hours}
                onChange={e => set('actual_rto_hours', e.target.value)}
                placeholder="e.g. 2.0"
              />
            </div>
          </div>

          <div className={styles.formRow}>
            <label className={styles.formLabel}>Lessons Learned</label>
            <textarea
              className={styles.formTextarea}
              rows={3}
              value={form.lessons_learned}
              onChange={e => set('lessons_learned', e.target.value)}
              placeholder="Key takeaways from this drill…"
            />
          </div>

          <div className={styles.formRow}>
            <label className={styles.formLabel}>Postmortem Notes</label>
            <textarea
              className={styles.formTextarea}
              rows={3}
              value={form.postmortem_notes}
              onChange={e => set('postmortem_notes', e.target.value)}
              placeholder="Root cause, action items…"
            />
          </div>

          <div className={styles.modalFooter}>
            <button type="button" className={styles.btnCancel} onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className={styles.btnSuccess} disabled={submitting}>
              {submitting
                ? <><Loader2 size={13} className={styles.spin} /> Saving…</>
                : <><CheckCircle2 size={13} /> Mark Complete</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function DRDrillsPage() {
  const [drills, setDrills] = useState<Drill[]>([])
  const [products, setProducts] = useState<Product[]>([])
  const [templates, setTemplates] = useState<DrillTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<string>('ALL')
  const [scheduleOpen, setScheduleOpen] = useState(false)
  const [completeTarget, setCompleteTarget] = useState<{ id: number; title: string } | null>(null)
  const [startingId, setStartingId] = useState<number | null>(null)

  const fetchAll = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [dRes, tRes, pRes] = await Promise.all([
        fetch(`${DR_API}/api/v1/dr/drills/`, { headers: authHeaders() }),
        fetch(`${DR_API}/api/v1/dr/drill-templates/`, { headers: authHeaders() }),
        fetch(`${DR_API}/api/v1/dr/products/?page_size=100`, { headers: authHeaders() }),
      ])
      if (!dRes.ok) throw new Error('Failed to load drills')
      const [dData, tData, pData] = await Promise.all([
        dRes.json(),
        tRes.ok ? tRes.json() : { results: [] },
        pRes.ok ? pRes.json() : { results: [] },
      ])
      setDrills(dData.results ?? dData)
      setTemplates(tData.results ?? tData)
      setProducts(pData.results ?? [])
    } catch {
      setError('Cannot reach DR API. Make sure the dr-api service is running on port 8001.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchAll() }, [fetchAll])

  async function handleStart(drill: Drill) {
    setStartingId(drill.id)
    try {
      await fetch(`${DR_API}/api/v1/dr/drills/${drill.id}/start/`, {
        method: 'POST',
        headers: authHeaders(),
      })
      fetchAll()
    } finally {
      setStartingId(null)
    }
  }

  // ── Derived ───────────────────────────────────────────────────────────────

  const filtered = statusFilter === 'ALL'
    ? drills
    : drills.filter(d => d.status === statusFilter)

  const scheduledCount  = drills.filter(d => d.status === 'SCHEDULED').length
  const inProgressCount = drills.filter(d => d.status === 'IN_PROGRESS').length
  const completedCount  = drills.filter(d => d.status === 'COMPLETED').length
  const failedCount     = drills.filter(d => d.status === 'FAILED').length

  return (
    <div className={styles.page}>

      {/* ── Header ── */}
      <div className={styles.hero}>
        <div className={styles.heroLeft}>
          <div className={styles.heroBreadcrumb}>
            <Activity size={12} />
            <span>Disaster Recovery</span>
            <span className={styles.breadSep}>/</span>
            <span>DR Drills</span>
          </div>
          <h1 className={styles.heroTitle}>DR Drill Management</h1>
          <p className={styles.heroSub}>
            Schedule, execute, and review disaster recovery drills across all products.
            Track SLA compliance and capture lessons learned.
          </p>
        </div>
        <div className={styles.heroActions}>
          <button className={styles.btnSecondary} onClick={fetchAll} disabled={loading}>
            <RefreshCw size={13} className={loading ? styles.spin : ''} /> Refresh
          </button>
          <button className={styles.btnPrimary} onClick={() => setScheduleOpen(true)}>
            <Plus size={14} /> Schedule Drill
          </button>
        </div>
      </div>

      {/* ── Error ── */}
      {error && (
        <div className={styles.errorBanner}>
          <AlertTriangle size={15} />
          <span>{error}</span>
          <button className={styles.retryBtn} onClick={fetchAll}>Retry</button>
        </div>
      )}

      {/* ── Summary strip ── */}
      {!loading && !error && (
        <div className={styles.summaryRow}>
          <div className={`${styles.summaryCard} ${styles.summaryCard_blue}`}>
            <Calendar size={16} />
            <div>
              <div className={styles.summaryValue}>{scheduledCount}</div>
              <div className={styles.summaryLabel}>Scheduled</div>
            </div>
          </div>
          <div className={`${styles.summaryCard} ${styles.summaryCard_amber}`}>
            <Play size={16} />
            <div>
              <div className={styles.summaryValue}>{inProgressCount}</div>
              <div className={styles.summaryLabel}>In Progress</div>
            </div>
          </div>
          <div className={`${styles.summaryCard} ${styles.summaryCard_green}`}>
            <CheckCircle2 size={16} />
            <div>
              <div className={styles.summaryValue}>{completedCount}</div>
              <div className={styles.summaryLabel}>Completed</div>
            </div>
          </div>
          <div className={`${styles.summaryCard} ${styles.summaryCard_red}`}>
            <AlertTriangle size={16} />
            <div>
              <div className={styles.summaryValue}>{failedCount}</div>
              <div className={styles.summaryLabel}>Failed</div>
            </div>
          </div>
        </div>
      )}

      {/* ── Drills table section ── */}
      <div className={styles.tableSection}>

        {/* Status filter tabs */}
        <div className={styles.tabRow}>
          {STATUS_FILTERS.map(f => (
            <button
              key={f.key}
              className={`${styles.tab} ${statusFilter === f.key ? styles.tabActive : ''}`}
              onClick={() => setStatusFilter(f.key)}
            >
              {f.label}
              <span className={styles.tabCount}>
                {f.key === 'ALL'
                  ? drills.length
                  : drills.filter(d => d.status === f.key).length}
              </span>
            </button>
          ))}
          <div className={styles.tabSpacer} />
          <span className={styles.resultCount}>{filtered.length} drills</span>
        </div>

        {/* Loading */}
        {loading && (
          <div className={styles.loadingRow}>
            <RefreshCw size={18} className={styles.spin} />
            <span>Loading drills…</span>
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && drills.length === 0 && (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}><ShieldAlert size={32} /></div>
            <h3>No drills scheduled yet</h3>
            <p>Schedule your first DR drill to start testing recovery procedures.</p>
            <button className={styles.btnPrimary} onClick={() => setScheduleOpen(true)}>
              <Plus size={13} /> Schedule First Drill
            </button>
          </div>
        )}

        {/* No match */}
        {!loading && drills.length > 0 && filtered.length === 0 && (
          <div className={styles.noMatch}>
            No drills with status <strong>{statusFilter}</strong>.{' '}
            <button onClick={() => setStatusFilter('ALL')}>Show all</button>
          </div>
        )}

        {/* Table */}
        {!loading && filtered.length > 0 && (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Drill</th>
                  <th>Product</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Scheduled</th>
                  <th>Duration</th>
                  <th>SLA</th>
                  <th className={styles.actionsCol}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(d => (
                  <tr
                    key={d.id}
                    className={styles.drillRow}
                    onClick={() => { window.location.href = `/disaster-recovery/drills/${d.id}` }}
                  >
                    <td>
                      <div className={styles.drillTitle}>{d.title}</div>
                    </td>
                    <td>
                      <span className={styles.productName}>{d.product_name}</span>
                    </td>
                    <td><TypeBadge type={d.drill_type} /></td>
                    <td><StatusBadge status={d.status} /></td>
                    <td className={styles.dateCell}>{fmtDate(d.scheduled_date)}</td>
                    <td className={styles.durationCell}>
                      {d.duration_minutes != null
                        ? <span className={styles.duration}>
                            <Clock size={11} /> {d.duration_minutes}m
                          </span>
                        : <span className={styles.notSet}>—</span>}
                    </td>
                    <td><SlaBadge compliant={d.sla_compliant} /></td>
                    <td
                      onClick={e => e.stopPropagation()}
                      className={styles.actionsCell}
                    >
                      {d.status === 'SCHEDULED' && (
                        <button
                          className={styles.btnStart}
                          disabled={startingId === d.id}
                          onClick={() => handleStart(d)}
                          title="Start drill"
                        >
                          {startingId === d.id
                            ? <Loader2 size={12} className={styles.spin} />
                            : <><Play size={11} /> Start</>}
                        </button>
                      )}
                      {d.status === 'IN_PROGRESS' && (
                        <button
                          className={styles.btnComplete}
                          onClick={() => setCompleteTarget({ id: d.id, title: d.title })}
                          title="Complete drill"
                        >
                          <CheckCircle2 size={11} /> Complete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Schedule modal ── */}
      {scheduleOpen && (
        <ScheduleDrillModal
          products={products}
          templates={templates}
          onCreated={() => { setScheduleOpen(false); fetchAll() }}
          onClose={() => setScheduleOpen(false)}
        />
      )}

      {/* ── Complete modal ── */}
      {completeTarget && (
        <CompleteDrillModal
          drillId={completeTarget.id}
          drillTitle={completeTarget.title}
          onCompleted={() => { setCompleteTarget(null); fetchAll() }}
          onClose={() => setCompleteTarget(null)}
        />
      )}
    </div>
  )
}
